import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { MultiSelect } from 'primereact/multiselect';

/**
 * Drop-in replacement for PrimeReact's <DataTable> that adds a column-visibility
 * chooser (a MultiSelect) into the table header.
 *
 * Usage: replace <DataTable ...> ... </DataTable> with
 *        <ColumnToggleDataTable ...> ... </ColumnToggleDataTable>
 * keeping the exact same <Column> children. No other change is required.
 *
 * Rules:
 *  - Only columns that expose a string `header` are user-toggleable and listed
 *    in the chooser. Columns without a header (expander / selection / action
 *    columns) are always rendered and never listed.
 *  - The chooser only appears when there is more than one toggleable column.
 *  - Any pre-existing `header` prop is preserved (rendered on the left, chooser
 *    on the right).
 *  - Hiding columns is display-only; it does not change the underlying data or
 *    any CSV/export built from `value`.
 */
const getColMeta = (child, idx) => {
    if (!child || !child.props) return null;
    const { field, header } = child.props;
    if (header === undefined || header === null || header === '') return null; // not toggleable
    const key = field || (typeof header === 'string' ? header : `col-${idx}`);
    const label = typeof header === 'string' ? header : String(field || key);
    return { key, label };
};

const ColumnToggleDataTable = forwardRef(({ children, header, columnToggle = true, ...rest }, ref) => {
    const childArray = React.Children.toArray(children);

    const toggleable = childArray.map((c, i) => getColMeta(c, i)).filter(Boolean);
    const allKeys = toggleable.map((c) => c.key);
    const keySig = allKeys.join('|');

    const [visible, setVisible] = useState(allKeys);

    // If the set of columns changes (e.g. dynamic columns after new data),
    // reset the visible set to "all".
    const prevSig = useRef(keySig);
    useEffect(() => {
        if (prevSig.current !== keySig) {
            prevSig.current = keySig;
            setVisible(allKeys);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keySig]);

    const renderedChildren = childArray.filter((child, idx) => {
        const meta = getColMeta(child, idx);
        if (!meta) return true;                       // always show headerless columns
        if (!allKeys.includes(meta.key)) return true; // safety
        return visible.includes(meta.key);
    });

    const chooser = (columnToggle && toggleable.length > 1) ? (
        <MultiSelect
            value={visible}
            options={toggleable}
            optionLabel="label"
            optionValue="key"
            onChange={(e) => setVisible(e.value && e.value.length ? e.value : allKeys)}
            placeholder="Columns"
            maxSelectedLabels={0}
            selectedItemsLabel={`${visible.length}/${allKeys.length} columns`}
            filter
            className="w-auto column-toggle-select"
            style={{ minWidth: '13rem' }}
            panelStyle={{ maxWidth: '20rem' }}
            scrollHeight="320px"
        />
    ) : null;

    const mergedHeader = (header || chooser) ? (
        <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
            <div className="flex-grow-1">{header || null}</div>
            {chooser && (
                <div className="d-flex align-items-center">
                    <span className="me-2 small text-secondary">Columns</span>
                    {chooser}
                </div>
            )}
        </div>
    ) : undefined;

    return (
        <DataTable ref={ref} {...rest} header={mergedHeader}>
            {renderedChildren}
        </DataTable>
    );
});

ColumnToggleDataTable.displayName = 'ColumnToggleDataTable';

export default ColumnToggleDataTable;
